import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output} from '@angular/core';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import {EmployeeStandardOverridesService} from '../../../services/employee-standard-overrides.service';
import {StandardAssignmentCleanupState, StandardAssignmentCleanupView} from '../../../models/employee-standard-overrides.model';
import {StandardCleanupTarget} from '../../../models/standard-distribution.model';
import {StandardVersionSnapshot} from '../../../services/standard-version';

const HTTP_FORBIDDEN = 403;
const MIN_STANDARD_VERSION = 0;
const BOOLEAN_TYPE = 'boolean';
const NO_CLEANUP_ERROR = '';
const ASSIGNMENT_MESSAGES = 'STANDARD_ASSIGNMENT_CLEANUP_';
const CONTENT_MESSAGES = 'STANDARD_CONTENT_RECOVERY_';
const RELOAD_LIST = 'STANDARD_RELOAD_LIST';
const RELOAD_DRAFT = 'STANDARD_RELOAD_REPLACE_DRAFT';
const RECOVERY_ERROR = {access: 'ACCESS_LOST', status: 'STATUS_FAILED', resume: 'RESUME_FAILED'};
const CLEANUP_ERROR = {
  status: 'STANDARD_ASSIGNMENT_CLEANUP_STATUS_FAILED'
};

@Component({
  selector: 'app-standard-assignment-cleanup',
  templateUrl: './standard-assignment-cleanup.component.html',
  styleUrls: ['./standard-assignment-cleanup.component.css']
})
export class StandardAssignmentCleanupComponent implements OnChanges, OnDestroy {
  @Input() targets: StandardCleanupTarget[] = [];
  @Input() standard: StandardVersionSnapshot & {number?: string};
  @Output() reloadRequested = new EventEmitter<void>();
  messagePrefix = ASSIGNMENT_MESSAGES;
  reloadKey = RELOAD_LIST;
  states: StandardAssignmentCleanupView[] = [];
  pending = false;
  statusKnown = false;
  cleanupPending = false;
  errorKey = NO_CLEANUP_ERROR;
  resumeErrorKey = NO_CLEANUP_ERROR;
  private capturedTargets: StandardCleanupTarget[] = [];
  private cancelled$ = new Subject<void>();

  constructor(private service: EmployeeStandardOverridesService) {}

  get visible(): boolean {
    return !this.standard || this.pending || !!this.errorKey || this.cleanupPending;
  }

  ngOnChanges(): void {
    this.cancelled$.next();
    this.pending = false;
    this.states = [];
    this.statusKnown = this.cleanupPending = false;
    this.resumeErrorKey = this.errorKey = NO_CLEANUP_ERROR;
    this.messagePrefix = this.standard ? CONTENT_MESSAGES : ASSIGNMENT_MESSAGES;
    this.reloadKey = this.standard ? RELOAD_DRAFT : RELOAD_LIST;
    const targets = this.standard && this.standard.id
      ? [{id: this.standard.id, label: this.standard.number || NO_CLEANUP_ERROR}] : this.targets;
    this.capturedTargets = targets.map(target => Object.assign({}, target));
    this.refreshStatus();
  }

  ngOnDestroy(): void {
    this.cancelled$.next();
    this.cancelled$.complete();
  }

  refreshStatus(): void {
    if (this.pending || !this.capturedTargets.length) { return; }
    this.pending = true;
    this.statusKnown = false;
    this.errorKey = NO_CLEANUP_ERROR;
    const requests = this.capturedTargets.map(target => this.service.fetchCleanupStatus(target.id)
      .map(response => this.readState(target, response)));
    Observable.forkJoin(requests).takeUntil(this.cancelled$).subscribe(
      states => this.acceptStates(states), error => this.handleStatusError(error)
    );
  }

  resume(state: StandardAssignmentCleanupView): void {
    if (this.pending || !this.statusKnown || !state.assignmentCleanupPending || !this.states.includes(state)) { return; }
    this.pending = true;
    this.statusKnown = false;
    this.resumeErrorKey = NO_CLEANUP_ERROR;
    this.service.resumeCleanup(state).takeUntil(this.cancelled$).subscribe(
      () => this.refreshAfterResume(),
      () => { this.resumeErrorKey = this.messagePrefix + RECOVERY_ERROR.resume; this.refreshAfterResume(); }
    );
  }

  reloadAssignments(): void {
    if (!this.pending && (!this.statusKnown || !this.cleanupPending)) { this.reloadRequested.emit(); }
  }

  trackStandard(index: number, state: StandardAssignmentCleanupView): string { return state.standardId; }

  private readState(target: StandardCleanupTarget,
                    response: HttpResponse<StandardAssignmentCleanupState>): StandardAssignmentCleanupView {
    const state = response.body;
    if (!state || state.standardId !== target.id || typeof state.assignmentCleanupPending !== BOOLEAN_TYPE
      || state.version == null || !Number.isSafeInteger(state.version) || state.version < MIN_STANDARD_VERSION) {
      throw new Error(CLEANUP_ERROR.status);
    }
    return Object.assign({}, state, {standardLabel: target.label});
  }

  private acceptStates(states: StandardAssignmentCleanupView[]): void {
    this.states = states;
    this.pending = false;
    this.statusKnown = true;
    this.cleanupPending = states.some(state => state.assignmentCleanupPending);
  }

  private handleStatusError(error: HttpErrorResponse): void {
    this.pending = false;
    this.statusKnown = false;
    this.errorKey = this.messagePrefix + (error && error.status === HTTP_FORBIDDEN ? RECOVERY_ERROR.access : RECOVERY_ERROR.status);
  }

  private refreshAfterResume(): void {
    this.pending = false;
    this.refreshStatus();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/standard-assignment-cleanup/standard-assignment-cleanup.component.ts